import { Injectable } from '@angular/core';
import { UserAgreementType } from '@flexyoo/api/models';
import { IEmployeeSettings } from '@flexyoo/employee/models';
import { ITenantSettings } from '@flexyoo/tenant/models';
import {
  IEmployeePlanInvitationInfo,
  isCafeteriaPlanEnrolled,
  isCafeteriaPlanInvited,
  isMobilityBudgetEnrolled,
  isMobilityBudgetInvited,
} from '../../util/employee-plans-info.util';

@Injectable({ providedIn: 'root' })
export class UserAgreementService {
  public checkUserAgreementState(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings,
    invitation: IEmployeePlanInvitationInfo | null | undefined
  ): 'under-validation' | 'to-be-uploaded' | 'sign-agreement-expired' | 'ok' {
    const applicableStates = this.getApplicableAgreementStates(
      employeeSettings,
      tenantSettings,
      invitation
    );

    if (applicableStates.length === 0) {
      return 'ok';
    }

    if (applicableStates.includes('under-validation')) {
      return 'under-validation';
    }

    if (applicableStates.includes('sign-agreement-expired')) {
      return 'sign-agreement-expired';
    }

    // Dual-plan: one accessible plan is enough to use the app
    if (applicableStates.includes('ok')) {
      return 'ok';
    }

    if (applicableStates.includes('to-be-uploaded')) {
      return 'to-be-uploaded';
    }

    return 'ok';
  }

  /**
   * True when at least one enrolled + invited plan still needs its agreement signed/uploaded.
   * Used for login to send dual-plan employees to the account page first,
   * even when another plan is already accessible.
   */
  public hasPendingUserAgreementUpload(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings,
    invitation: IEmployeePlanInvitationInfo | null | undefined
  ): boolean {
    return this.getApplicableAgreementStates(employeeSettings, tenantSettings, invitation).includes(
      'to-be-uploaded'
    );
  }

  /**
   * True when login should land on the account page before the default route.
   * Skips the redirect when an enrolled plan is accessible without agreement signing.
   */
  public shouldRedirectToAccountOnLogin(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings,
    invitation: IEmployeePlanInvitationInfo | null | undefined
  ): boolean {
    const applicableStates = this.getApplicableAgreementStates(
      employeeSettings,
      tenantSettings,
      invitation
    );

    if (!applicableStates.includes('to-be-uploaded')) {
      return false;
    }

    if (!applicableStates.includes('ok')) {
      return true;
    }

    return !this.hasPlanAccessibleWithoutAgreementRequirement(
      employeeSettings,
      tenantSettings,
      invitation
    );
  }

  private getApplicableAgreementStates(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings,
    invitation: IEmployeePlanInvitationInfo | null | undefined
  ): ('under-validation' | 'to-be-uploaded' | 'sign-agreement-expired' | 'ok')[] {
    const applicableStates: (
      | 'under-validation'
      | 'to-be-uploaded'
      | 'sign-agreement-expired'
      | 'ok'
    )[] = [];

    if (isCafeteriaPlanEnrolled(employeeSettings) && isCafeteriaPlanInvited(invitation)) {
      if (tenantSettings.useCafeteriaAgreement) {
        applicableStates.push(
          this.checkCafeteriaUserAgreementState(employeeSettings, tenantSettings)
        );
      } else {
        applicableStates.push('ok');
      }
    }

    if (isMobilityBudgetEnrolled(employeeSettings) && isMobilityBudgetInvited(invitation)) {
      if (tenantSettings.useMobilityBudgetAgreement) {
        applicableStates.push(
          this.checkMobilityBudgetUserAgreementState(employeeSettings, tenantSettings)
        );
      } else {
        applicableStates.push('ok');
      }
    }

    return applicableStates;
  }

  private hasPlanAccessibleWithoutAgreementRequirement(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings,
    invitation: IEmployeePlanInvitationInfo | null | undefined
  ): boolean {
    if (
      isCafeteriaPlanEnrolled(employeeSettings) &&
      isCafeteriaPlanInvited(invitation) &&
      !tenantSettings.useCafeteriaAgreement
    ) {
      return true;
    }

    if (
      isMobilityBudgetEnrolled(employeeSettings) &&
      isMobilityBudgetInvited(invitation) &&
      !tenantSettings.useMobilityBudgetAgreement
    ) {
      return true;
    }

    return false;
  }

  public checkCafeteriaUserAgreementState(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings
  ): 'under-validation' | 'to-be-uploaded' | 'sign-agreement-expired' | 'ok' {
    if (!isCafeteriaPlanEnrolled(employeeSettings)) {
      return 'ok';
    }

    if (tenantSettings.useCafeteriaAgreement) {
      if (
        !employeeSettings.cafeteriaUserAgreement?.ApprovedDate &&
        employeeSettings.cafeteriaUserAgreement?.AppFileId
      ) {
        return 'under-validation';
      }

      const primesAndBonusesAgreementUploaded =
        !employeeSettings.isPrimesAndBonusesFinancingPlan ||
        [UserAgreementType.Primes, UserAgreementType.PrimesAndSalarySacrifice].includes(
          employeeSettings.cafeteriaUserAgreement?.Type
        );

      const salarySacrificeAgreementUploaded =
        !employeeSettings.isSalarySacrificeFinancingPlan ||
        [UserAgreementType.SalarySacrifice, UserAgreementType.PrimesAndSalarySacrifice].includes(
          employeeSettings.cafeteriaUserAgreement?.Type
        );

      const someAgreementIsSigned =
        employeeSettings.isPrimesAndBonusesFinancingPlan &&
        employeeSettings.isSalarySacrificeFinancingPlan &&
        employeeSettings.cafeteriaUserAgreement?.AppFileId;

      if (someAgreementIsSigned) {
        return 'ok';
      }

      if (
        !employeeSettings.cafeteriaUserAgreement?.AppFileId ||
        !primesAndBonusesAgreementUploaded ||
        !salarySacrificeAgreementUploaded
      ) {
        if (
          !employeeSettings.userAgreementSignEndDate ||
          new Date(employeeSettings.userAgreementSignEndDate) >= new Date()
        ) {
          return 'to-be-uploaded';
        } else {
          return 'sign-agreement-expired';
        }
      }

      return 'ok';
    }

    return 'ok';
  }

  public checkMobilityBudgetUserAgreementState(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings
  ): 'under-validation' | 'to-be-uploaded' | 'sign-agreement-expired' | 'ok' {
    if (!isMobilityBudgetEnrolled(employeeSettings)) {
      return 'ok';
    }

    if (tenantSettings.useMobilityBudgetAgreement) {
      if (
        !employeeSettings.mobilityBudgetUserAgreement?.ApprovedDate &&
        employeeSettings.mobilityBudgetUserAgreement?.AppFileId
      ) {
        return 'under-validation';
      }

      if (
        employeeSettings.mobilityBudgetUserAgreement?.ApprovedDate ||
        employeeSettings.mobilityBudgetUserAgreement?.AppFileId
      ) {
        return 'ok';
      }

      if (
        !employeeSettings.userAgreementSignEndDate ||
        new Date(employeeSettings.userAgreementSignEndDate) >= new Date()
      ) {
        return 'to-be-uploaded';
      } else {
        return 'sign-agreement-expired';
      }
    }

    return 'ok';
  }
}
